/** @jsx jsx */

import PropTypes from "prop-types"
import { jsx } from "theme-ui"
import { useState, useRef, useEffect } from "react"

import theme from "../gatsby-plugin-theme-ui"
import { Layout } from "../components/Layout"
import {
  CrystallizeItemPropTypes,
  CrystallizeProductPropTypes,
} from "../utils/propTypes"
import { extractBaseSeoFields } from "../utils/seoUtils"
import { transformProduct } from "../utils/eCommerceUtils"
import { ProductDetails } from "../components/product/ProductDetails"
import { OrderForm } from "../components/product/OrderForm"
import { OrderConfirmationSection } from "../components/product/OrderConfirmationSection"

export const ProductPageTemplate = ({ data, location }) => {
  const product = transformProduct(data.crystallize.product)
  const [showOrderForm, updateShowOrderForm] = useState(false)
  const [selectionState, updateSelectionState] = useState({
    cost: product.defaultVariant.price,
    variantSku: product.defaultVariant.sku,
    quantity: 1,
  })
  const [orderDetails, updateOrderDetails] = useState({
    orderNumber: null,
    orderTotal: null,
    paymentMode: null,
    orderStageUpdateFailed: false,
  })
  const showOrderConfirmation = orderDetails.orderNumber
  const orderFormRef = useRef()
  const orderConfirmationRef = useRef()

  useEffect(() => {
    if (showOrderForm) {
      orderFormRef.current.focus()
      document.activeElement.scrollIntoView({
        behavior: "smooth",
      })
    }

    if (showOrderConfirmation) {
      orderConfirmationRef.current.focus()
      document.activeElement.scrollIntoView({
        behavior: "smooth",
      })
    }
  }, [showOrderForm, showOrderConfirmation])

  const seoProps = {
    ...extractBaseSeoFields({
      // Second image should be optimized for opengraph image dimensions, but we fallback
      // to the main image if it doesn't exist
      opengraph_image:
        product.defaultVariant.images[1] || product.defaultVariant.images[0],
      meta_title: `Product - ${product.name}`,
      meta_description: product.components.summary.content.text,
      canonical_url: location.pathname,
    }),
    scripts: ["https://js.stripe.com/v3/"],
  }

  return (
    <Layout seoProps={seoProps}>
      <ProductDetails
        product={product}
        selectionState={selectionState}
        updateSelectionState={updateSelectionState}
        onCheckout={() => updateShowOrderForm(true)}
      />
      <OrderForm
        product={product}
        selectionState={selectionState}
        updateOrderDetails={updateOrderDetails}
        containerStyles={{
          ...theme.basePageStyles.contentContainer,
          transition: "all 0.5s ease-out",
          maxHeight: showOrderForm ? "1500px" : "0",
          overflow: "hidden",
          "*": {
            display: showOrderForm ? null : "none !important",
          },
        }}
        containerRef={orderFormRef}
      />
      <OrderConfirmationSection
        {...orderDetails}
        containerStyles={{
          ...theme.basePageStyles.contentContainer,
          transition: "all 0.5s ease-out",
          maxHeight: showOrderConfirmation ? "1500px" : "0",
          my: showOrderConfirmation ? 7 : "0",
          overflow: "hidden",
          "*": {
            display: showOrderConfirmation ? null : "none !important",
          },
        }}
        containerRef={orderConfirmationRef}
      />
    </Layout>
  )
}
ProductPageTemplate.propTypes = {
  data: PropTypes.shape({
    crystallize: PropTypes.shape({
      product: PropTypes.shape({
        ...CrystallizeItemPropTypes,
        ...CrystallizeProductPropTypes,
      }),
    }),
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
}
